<script setup lang="ts">
import { sanitizeHtml } from '@/shared/utils/sanitizeHtml';

import type { IJournalEditorBlock } from '@/entities/journal/model/types';

defineProps<{
  blocks: IJournalEditorBlock[];
}>();

const headerTag = (level?: number): string => {
  if (level === 3) return 'h3';
  if ((level ?? 0) >= 4) return 'h4';
  return 'h2';
};

const listTag = (style?: string): string => (style === 'ordered' ? 'ol' : 'ul');

const listItemText = (item: string | { content?: string }): string =>
  sanitizeHtml(typeof item === 'string' ? item : item.content);
</script>

<template>
  <div class="article-blocks">
    <template v-for="(block, index) in blocks" :key="block.id ?? index">
      <p
        v-if="block.type === 'paragraph'"
        class="article-blocks__paragraph"
        v-html="sanitizeHtml(block.data.text)"
      ></p>
      <h3
        v-else-if="block.type === 'header' && headerTag(block.data.level) === 'h3'"
        class="article-blocks__header"
        :data-id="block.id"
        v-html="sanitizeHtml(block.data.text)"
      ></h3>
      <h4
        v-else-if="block.type === 'header' && headerTag(block.data.level) === 'h4'"
        class="article-blocks__header"
        :data-id="block.id"
        v-html="sanitizeHtml(block.data.text)"
      ></h4>
      <h2
        v-else-if="block.type === 'header'"
        class="article-blocks__header"
        :data-id="block.id"
        v-html="sanitizeHtml(block.data.text)"
      ></h2>
      <component
        :is="listTag(block.data.style)"
        v-else-if="block.type === 'list'"
        :class="['article-blocks__list', { _ordered: block.data.style === 'ordered' }]"
      >
        <li
          v-for="(item, itemIndex) in block.data.items"
          :key="itemIndex"
          class="article-blocks__list-item"
          v-html="listItemText(item)"
        ></li>
      </component>
      <figure v-else-if="block.type === 'image'" class="article-blocks__figure">
        <NuxtImg
          class="article-blocks__image"
          :src="block.data.file?.url ?? block.data.url"
          :alt="block.data.caption ?? 'иллюстрация к статье'"
          width="800"
          densities="x1 x2"
          loading="lazy"
        />
      </figure>
      <blockquote v-else-if="block.type === 'quote'" class="article-blocks__quote">
        <AppIcon class="article-blocks__quote-icon _left" name="QuoteMarkLeft" /><span
          v-html="sanitizeHtml(block.data.text)"
        ></span
        ><AppIcon class="article-blocks__quote-icon _right" name="QuoteMarkRight" />
      </blockquote>
    </template>
  </div>
</template>

<style lang="less">
.article-blocks {
  .flex-column(@size-x5);

  word-break: break-word;

  a {
    text-decoration: underline;
    color: @color-persimmon;

    &:hover {
      text-decoration: none;
    }
  }

  &__paragraph {
    font-size: @size-x4;
    line-height: 22px;
    font-weight: 400;
  }

  &__header {
    .h-offset-reset();

    font-size: @size-x6;
    line-height: 31px;
    font-weight: 700;
  }

  &__list {
    .flex-column(@size-x3);

    list-style: disc;
    list-style-position: inside;
    padding: 0;
    margin: 0;

    &._ordered {
      list-style: decimal;
      list-style-position: inside;
    }
  }

  &__list-item {
    font-size: @size-x4;
    line-height: 22px;
    font-weight: 400;
  }

  &__figure {
    display: flex;
    margin: 0;
  }

  &__image {
    max-width: 100%;
    max-height: 425px;
    object-fit: contain;
  }

  &__quote {
    font-size: @size-x4;
    line-height: 22px;
    background-color: @color-pippin;
    border-left: @main-size solid @color-persimmon;
    padding: @size-x5;
    margin: 0;
  }

  &__quote-icon {
    width: @size-x4;
    height: @size-x4;

    &._left {
      margin-right: @size-x3;
    }

    &._right {
      margin-left: @size-x3;
    }
  }
}
</style>
